import WorkbookPickForm from "./WorkbookPickForm";
import WorkbookInlineQuestionPick from "./WorkbookInlineQuestionPick";
import { useWorkbookPickState } from "../hooks/useWorkbookPickState";
import { Notebook, CheckCircle } from "@phosphor-icons/react";

/**
 * WorkbookInPaneRenderer — renders a full workbook (markdown article with
 * inline picks per question + slim bottom submit bar) INSIDE the master-
 * detail right pane. Replaces the standalone /workbook/<id> dedicated page.
 *
 * Phase 12E (cycle-5c+25-fix8) per Owner direction:
 *   "a list of all roadmap items on the left, that when clicked open up
 *    the page on the right so not to go to new tab, would be handy"
 *
 * Lifted from `pages/WorkbookDetailPage.jsx` — same render logic, same hook,
 * same `renderMarkdownWithInlinePicks` walker, but rendered without TopBar /
 * back-link chrome since the master-detail page provides those.
 */

const Q_HEADING_RE = /^###\s+(Q\d+)\b/;

function renderInline(text) {
  if (!text) return null;
  const parts = [];
  let remaining = text;
  let key = 0;
  const patterns = [
    {
      re: /`([^`]+)`/,
      render: (m) => (
        <code
          key={key++}
          className="text-mono px-1 py-0.5 rounded"
          style={{
            fontSize: "0.8em",
            background: "hsl(var(--muted))",
            color: "hsl(var(--foreground))",
          }}
        >
          {m[1]}
        </code>
      ),
    },
    {
      re: /\*\*([^*]+)\*\*/,
      render: (m) => <strong key={key++}>{m[1]}</strong>,
    },
    {
      re: /\[([^\]]+)\]\(([^)]+)\)/,
      render: (m) => (
        <a
          key={key++}
          href={m[2]}
          target="_blank"
          rel="noreferrer"
          className="underline"
          style={{ color: "hsl(var(--primary))" }}
        >
          {m[1]}
        </a>
      ),
    },
  ];
  let safety = 0;
  while (remaining && safety++ < 200) {
    let earliest = null;
    let earliestPat = null;
    for (const pat of patterns) {
      const m = remaining.match(pat.re);
      if (m && (earliest === null || m.index < earliest.index)) {
        earliest = m;
        earliestPat = pat;
      }
    }
    if (!earliest) {
      parts.push(remaining);
      break;
    }
    if (earliest.index > 0) parts.push(remaining.slice(0, earliest.index));
    parts.push(earliestPat.render(earliest));
    remaining = remaining.slice(earliest.index + earliest[0].length);
  }
  return parts;
}

function renderMarkdownWithInlinePicks(md, injectAfterQ) {
  if (!md) return [];
  const lines = md.split("\n");
  const out = [];
  let tableRows = [];
  let inTable = false;
  let listBuffer = [];
  let inCode = false;
  let codeBuffer = [];
  let currentQNum = null;
  let key = 0;

  const flushList = () => {
    if (listBuffer.length) {
      out.push(
        <ul
          key={`ul-${key++}`}
          className="list-disc pl-5 my-2 space-y-1 text-sm"
          style={{ color: "hsl(var(--foreground))" }}
        >
          {listBuffer.map((item, idx) => (
            <li key={idx}>{renderInline(item)}</li>
          ))}
        </ul>,
      );
      listBuffer = [];
    }
  };
  const flushTable = () => {
    if (tableRows.length) {
      const [header, , ...body] = tableRows;
      out.push(
        <div key={`tbl-${key++}`} className="my-3 overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr style={{ background: "hsl(var(--muted))" }}>
                {header.map((h, i) => (
                  <th
                    key={i}
                    className="border p-2 text-left"
                    style={{ borderColor: "hsl(var(--border))", color: "hsl(var(--foreground))" }}
                  >
                    {renderInline(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((row, ri) => (
                <tr key={ri}>
                  {row.map((c, ci) => (
                    <td
                      key={ci}
                      className="border p-2 align-top"
                      style={{ borderColor: "hsl(var(--border))", color: "hsl(var(--foreground))" }}
                    >
                      {renderInline(c)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      tableRows = [];
      inTable = false;
    }
  };
  const flushCode = () => {
    if (codeBuffer.length) {
      out.push(
        <pre
          key={`code-${key++}`}
          className="my-3 overflow-x-auto rounded-md p-3 text-mono"
          style={{
            fontSize: "0.75rem",
            background: "hsl(var(--muted))",
            color: "hsl(var(--foreground))",
            border: "1px solid hsl(var(--border))",
          }}
        >
          <code>{codeBuffer.join("\n")}</code>
        </pre>,
      );
      codeBuffer = [];
    }
  };

  const emitInlinePickIfInQ = () => {
    if (!currentQNum) return;
    flushList();
    flushTable();
    const widget = injectAfterQ ? injectAfterQ(currentQNum) : null;
    if (widget) {
      out.push(<div key={`pick-${currentQNum}-${key++}`}>{widget}</div>);
    }
    currentQNum = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    if (raw.trim().startsWith("```")) {
      if (inCode) { flushCode(); inCode = false; }
      else { flushList(); flushTable(); inCode = true; }
      continue;
    }
    if (inCode) { codeBuffer.push(raw); continue; }
    if (raw.trim().startsWith("|") && raw.includes("|", 1)) {
      flushList();
      const cells = raw.split("|").slice(1, -1).map((c) => c.trim());
      tableRows.push(cells);
      inTable = true;
      continue;
    } else if (inTable) { flushTable(); }
    if (/^\s*[-*]\s+/.test(raw)) {
      listBuffer.push(raw.replace(/^\s*[-*]\s+/, ""));
      continue;
    } else if (listBuffer.length) { flushList(); }
    if (raw.startsWith("# ") || raw.startsWith("## ") || raw.startsWith("### ")) {
      emitInlinePickIfInQ();
    }
    if (raw.startsWith("# ")) {
      out.push(<h1 key={key++} className="text-xl sm:text-2xl font-semibold mt-6 mb-3 break-words" style={{ color: "hsl(var(--foreground))" }}>{renderInline(raw.slice(2))}</h1>);
      continue;
    }
    if (raw.startsWith("## ")) {
      out.push(<h2 key={key++} className="text-lg sm:text-xl font-semibold mt-5 mb-2 break-words" style={{ color: "hsl(var(--foreground))" }}>{renderInline(raw.slice(3))}</h2>);
      continue;
    }
    if (raw.startsWith("### ")) {
      const qMatch = raw.match(Q_HEADING_RE);
      if (qMatch) currentQNum = qMatch[1];
      out.push(<h3 key={key++} className="text-sm sm:text-base font-semibold mt-4 mb-1.5 break-words" style={{ color: "hsl(var(--foreground))" }}>{renderInline(raw.slice(4))}</h3>);
      continue;
    }
    if (raw.startsWith("---")) {
      out.push(<hr key={key++} className="my-4" style={{ borderColor: "hsl(var(--border))" }} />);
      continue;
    }
    if (raw.trim() === "") {
      out.push(<div key={key++} className="h-2" />);
      continue;
    }
    out.push(<p key={key++} className="text-sm leading-relaxed my-1" style={{ color: "hsl(var(--foreground))" }}>{renderInline(raw)}</p>);
  }
  flushList();
  flushTable();
  flushCode();
  emitInlinePickIfInQ();
  return out;
}

export default function WorkbookInPaneRenderer({ workbookId, content, parsed, onSubmitted }) {
  const pickState = useWorkbookPickState({ parsed, workbookId, onSubmitted });
  const questionByNumber = (parsed?.questions || []).reduce((acc, q) => {
    acc[q.number] = q;
    return acc;
  }, {});
  const injectAfterQ = (qNum) => {
    const q = questionByNumber[qNum];
    if (!q) return null;
    return <WorkbookInlineQuestionPick question={q} state={pickState} />;
  };

  return (
    <div data-testid="workbook-in-pane-renderer">
      <div
        className="flex items-center gap-2 mb-3 pb-2 border-b flex-wrap"
        style={{ borderColor: "hsl(var(--border))" }}
      >
        <Notebook size={14} style={{ color: "hsl(var(--primary))" }} />
        <code
          className="text-mono break-all min-w-0 flex-1"
          style={{ fontSize: "0.6rem", color: "hsl(var(--muted-foreground))" }}
        >
          MP_INBOX/{workbookId}.md
        </code>
        {parsed && (
          <span
            className="text-mono shrink-0"
            style={{ fontSize: "0.6rem", color: "hsl(var(--muted-foreground))" }}
          >
            Parser: {parsed.parse_quality} · {parsed.questions?.length || 0} Qs
          </span>
        )}
      </div>
      {pickState.priorAck && (
        <div
          data-testid="workbook-prior-ack-banner"
          className="flex items-start gap-2 mb-4 p-3 rounded-md border"
          style={{
            background: "hsl(var(--primary) / 0.06)",
            borderColor: "hsl(var(--primary) / 0.25)",
          }}
        >
          <CheckCircle
            size={16}
            weight="fill"
            style={{ color: "hsl(var(--primary))", flexShrink: 0, marginTop: 2 }}
          />
          <div style={{ fontSize: "0.85rem", lineHeight: 1.45 }}>
            <div style={{ color: "hsl(var(--foreground))", fontWeight: 500 }}>
              You previously submitted picks on this workbook
            </div>
            <div style={{ color: "hsl(var(--muted-foreground))", marginTop: 2 }}>
              Submitted{" "}
              {pickState.priorAck.submitted_at && (
                <time
                  dateTime={pickState.priorAck.submitted_at}
                  style={{ color: "hsl(var(--foreground))" }}
                >
                  {new Intl.DateTimeFormat(undefined, {
                    month: "short",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                    timeZoneName: "short",
                  }).format(new Date(pickState.priorAck.submitted_at))}
                </time>
              )}
              . Your prior picks are pre-filled below — edit any and re-submit to update,
              or close and leave them as-is.
            </div>
          </div>
        </div>
      )}
      <div className="prose-content">
        {renderMarkdownWithInlinePicks(content, injectAfterQ)}
      </div>
      {parsed && parsed.questions && parsed.questions.length > 0 && (
        <WorkbookPickForm state={pickState} />
      )}
    </div>
  );
}
